import { StyleCreate, StyleMeta } from '@dicebear/core';
import { BACKGROUND_COLORS, SHAPE_COLORS } from './colors';
import * as ShapeComponents from './shapes';

export const meta: StyleMeta = {
  title: 'Staff Member Avatars',
  creator: 'NextIsFree',
  source: 'https://github.com/next-is-free',
  license: {
    name: 'Custom',
    url: ''
  }
};

type Options = {
  backgroundColor?: string[];
};

// Helper function to render React component to SVG string and extract viewBox
function renderShapeToSvg(ShapeComponent: any, color: string): { content: string; viewBox: string } {
  const shapeElement = ShapeComponent({ width: 32 });

  // Extract viewBox from the SVG element
  const viewBox = shapeElement.props.viewBox || '0 0 32 32';

  // Extract the SVG content from React element
  const extractContent = (element: any): string => {
    if (!element) return '';

    if (typeof element === 'string') return element;

    if (Array.isArray(element)) {
      return element.map(extractContent).join('');
    }

    const { type, props } = element;

    if (!props) return '';

    // Handle different SVG elements
    if (type === 'path') {
      const fillRule = props['fill-rule'] ? ` fill-rule="${props['fill-rule']}"` : '';
      const clipRule = props['clip-rule'] ? ` clip-rule="${props['clip-rule']}"` : '';
      // Preserve original fill if it's not currentColor, otherwise use the provided color
      const fillColor = props.fill && props.fill !== 'currentColor' ? props.fill : `#${color}`;
      return `<path d="${props.d}" fill="${fillColor}"${fillRule}${clipRule}/>`;
    }

    if (type === 'g') {
      const clipPath = props.clipPath ? ` clip-path="url(#${props.clipPath.replace('url(#', '').replace(')', '')})"` : '';
      const children = extractContent(props.children);
      return `<g${clipPath}>${children}</g>`;
    }

    if (type === 'rect') {
      return `<rect width="${props.width}" height="${props.height}" fill="${props.fill || '#' + color}"/>`;
    }

    if (type === 'circle') {
      return `<circle cx="${props.cx}" cy="${props.cy}" r="${props.r}" fill="${props.fill || '#' + color}"/>`;
    }

    if (type === 'polygon') {
      return `<polygon points="${props.points}" fill="${props.fill || '#' + color}"/>`;
    }

    if (type === 'clipPath') {
      const id = props.id;
      const children = extractContent(props.children);
      return `<clipPath id="${id}">${children}</clipPath>`;
    }

    if (type === 'defs') {
      const children = extractContent(props.children);
      return `<defs>${children}</defs>`;
    }

    // Handle children recursively
    if (props.children) {
      return extractContent(props.children);
    }

    return '';
  };

  return {
    content: extractContent(shapeElement.props.children),
    viewBox
  };
}

// Extract all shape names (Shape1, Shape2, etc.)
const shapeNames = Object.keys(ShapeComponents).filter(key => key.startsWith('Shape'));

export const create: StyleCreate<Options> = ({ prng, options }) => {
  // Get options with defaults
  const backgroundColor = options?.backgroundColor ?? BACKGROUND_COLORS;

  // Pick random colors
  const bgColor = prng.pick(backgroundColor);
  const fgColor = SHAPE_COLORS[BACKGROUND_COLORS.indexOf(bgColor as string)];

  // Pick a random shape
  const shapeName = prng.pick(shapeNames);
  const ShapeComponent = (ShapeComponents as any)[shapeName as string];

  // Render the shape with the selected color and get its viewBox
  const { content: shapeContent, viewBox } = renderShapeToSvg(ShapeComponent, fgColor as string);

  // Parse the viewBox to get the shape's dimensions
  const viewBoxParts = viewBox.split(' ').map(Number);
  const shapeWidth = viewBoxParts[2];
  const shapeHeight = viewBoxParts[3];

  // Calculate scale to fit the shape in a 32x32 container with padding
  // We want the shape to be 55% of the container size (leaving 22.5% padding on each side)
  const targetSize = 32 * 0.8; // 55% of container
  const scaleX = targetSize / shapeWidth;
  const scaleY = targetSize / shapeHeight;
  const scale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

  // Calculate translation to center the scaled shape
  const scaledWidth = shapeWidth * scale;
  const scaledHeight = shapeHeight * scale;
  const translateX = (32 - scaledWidth) / 2 - viewBoxParts[0] * scale;
  const translateY = (32 - scaledHeight) / 2 - viewBoxParts[1] * scale;

  return {
    attributes: {
      viewBox: '0 0 32 32',
      fill: 'none',
      'shape-rendering': 'auto'
    },
    body: `
      <rect width="32" height="32" fill="#${bgColor}"/>
      <g transform="translate(${translateX}, ${translateY}) scale(${scale})">
        ${shapeContent}
      </g>
    `,
    extra: () => ({
      meta: {
        shapeId: shapeName,
        backgroundColor: bgColor,
        shapeColor: fgColor
      }
    })
  };
};

export const schema = {
  properties: {
    backgroundColor: {
      type: 'array',
      items: {
        type: 'string',
        pattern: '^[0-9A-Fa-f]{6}$'
      },
      default: BACKGROUND_COLORS
    },
    shapeColor: {
      type: 'array',
      items: {
        type: 'string',
        pattern: '^[0-9A-Fa-f]{6}$'
      },
      default: SHAPE_COLORS
    }
  }
} as const;
